// WYVIO UI 1.0 — Pilotage V4 (Dashboard V3)
// Routes canoniques : /app + /app/dashboard  ·  Alias : /app/dashboard-v3
// Aucun motion. Aucune logique métier modifiée pendant la propagation.

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, ArrowRight, ChevronDown } from "lucide-react";

import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppSidebar } from "@/components/AppSidebar";
import { SilhouetteVehicle } from "@/components/wyvio-ui/signature/SilhouetteVehicle";
import PilotageMandatsPanel from "@/components/pilotage/PilotageMandatsPanel";

/* ═════════════════════════ Utils ═══════════════════════════ */
const guessSilhouette = (name = "") => {
  const v = name.toLowerCase();
  if (/gt3|coupe|coupé|911|m2|m4|corvette|amg gt|f-type|cayman/.test(v)) return "coupe";
  if (/suv|q3|q5|q7|q8|x1|x3|x5|x7|macan|cayenne|glc|gle|gls|3008|5008|2008|rav|kodiaq/.test(v)) return "suv";
  if (/rs6|break|estate|touring|allroad|shooting|avant/.test(v)) return "break";
  return "berline";
};

const daysUntil = (iso) => {
  try {
    const d = new Date(iso).getTime();
    return Math.max(0, Math.round((d - Date.now()) / 86400000));
  } catch { return 0; }
};

// Sprint P0.1 — humanisation d'un ISO en "lu il y a X min/h/j".
const humanAgo = (iso) => {
  if (!iso) return "";
  const ts = new Date(iso).getTime();
  if (isNaN(ts)) return "";
  const diffMin = Math.max(0, Math.round((Date.now() - ts) / 60000));
  if (diffMin < 1)  return "à l'instant";
  if (diffMin < 60) return `il y a ${diffMin} min`;
  const diffH = Math.round(diffMin / 60);
  if (diffH < 24)   return `il y a ${diffH} h`;
  const diffD = Math.round(diffH / 24);
  return `il y a ${diffD} j`;
};

const archetypeOf = (item, totals) => {
  const step = item.step_key || "";
  const action = item.action || "";
  const isUnpaidByName = /unpaid|paiement|impaye/i.test(item.vehicle_name || "");
  // Sprint P0.1 — action standalone "reply_expected" pour dossiers sans autre priorité workflow.
  if (action === "reply_expected") {
    if (item.unread_count > 0) {
      return { kind: "response", reason: `${item.unread_count} message${item.unread_count > 1 ? "s" : ""} en attente de réponse`, cta: "Répondre", dot: "#EAB308" };
    }
    return { kind: "response", reason: `Réponse attendue · lu ${humanAgo(item.last_read_by_agency_at)}`, cta: "Répondre", dot: "#EAB308" };
  }
  if (step === "paiement" || isUnpaidByName) {
    const dj = daysUntil(item.delivery_date);
    // Sprint UI 1.0 — 4 cas distincts, ne pas afficher systématiquement "Confirmer paiement".
    // confirmed → n'apparaît normalement pas ici (step déjà completed). Filet de sécurité.
    if (item.payment_status === "confirmed") {
      return { kind: "payment", reason: "Paiement confirmé", cta: "Ouvrir le dossier", dot: "#B8E600" };
    }
    if (item.payment_status === "declared") {
      return { kind: "payment", reason: "Client a déclaré avoir payé", cta: "Vérifier le paiement", dot: "#8B5CF6" };
    }
    if (item.payment_settle_in_agency) {
      const near = dj != null && dj <= 2;
      return {
        kind: "payment",
        reason: near
          ? `Règlement prévu en agence — livraison dans ${Math.max(0, dj)} jour${dj > 1 ? "s" : ""}`
          : "Règlement prévu en agence",
        cta: near ? "Confirmer paiement" : "Ouvrir le dossier",
        dot: "#6B7280",
      };
    }
    if (item.payment_reminder_due || item.payment_controls_completed) {
      return {
        kind: "payment",
        reason: item.delivery_date
          ? `Solde à réclamer — livraison dans ${dj} jour${dj > 1 ? "s" : ""}`
          : "Solde à réclamer avant livraison",
        cta: "Rappeler le paiement",
        dot: "#C8FF2F",
      };
    }
    // Fallback : contrôles pas encore terminés → simple mention
    return { kind: "payment", reason: "Paiement en attente", cta: "Ouvrir le dossier", dot: "#94A3B8" };
  }
  if (step === "livraison") {
    return { kind: "delivery", reason: "Véhicule prêt — livraison à proposer", cta: "Proposer une livraison", dot: "#94A3B8" };
  }
  if (step === "ct" || /ct|contrôle/i.test(item.step_label || "")) {
    return { kind: "delivery", reason: "Contrôle technique à planifier", cta: "Planifier", dot: "#94A3B8" };
  }
  if (item.unread_count > 0 || item.awaiting_reply || totals?.unread_messages > 0) {
    if (item.unread_count > 0) {
      return { kind: "response", reason: `${item.unread_count} message${item.unread_count > 1 ? "s" : ""} en attente de réponse`, cta: "Répondre", dot: "#EAB308" };
    }
    if (item.awaiting_reply) {
      return { kind: "response", reason: `Réponse attendue · lu ${humanAgo(item.last_read_by_agency_at)}`, cta: "Répondre", dot: "#EAB308" };
    }
    return { kind: "response", reason: `1 message en attente de réponse`, cta: "Répondre", dot: "#EAB308" };
  }
  return { kind: "other", reason: item.step_label || "Action requise", cta: "Ouvrir", dot: "#94A3B8" };
};

const FR_WEEKDAY = (iso) => {
  const d = iso ? new Date(iso) : new Date();
  return d.toLocaleDateString("fr-FR", { weekday: "long" }).toUpperCase();
};
const FR_DAY_MONTH = (iso) => {
  const d = iso ? new Date(iso) : new Date();
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "long" }).toUpperCase();
};

/* ═════════════════════════ Sidebar sombre ══════════════════ */
// Sidebar mutualisée depuis /components/AppSidebar (Sprint P0.1).

/* ═════════════════════════ KPI géant (V4) ══════════════════ */
const KpiGiant = ({ value, label, sub, accent, testId }) => (
  <div className="flex flex-col" data-testid={testId}>
    <div className="text-[56px] font-medium leading-none tracking-[-0.03em] text-black">
      {String(value).padStart(2, "0")}
    </div>
    <div className="mt-3 flex items-center gap-1.5">
      <span className="h-[6px] w-[6px] rounded-full" style={{ background: accent }} />
      <span className="text-[10.5px] uppercase tracking-[0.16em] text-black/60">{label}</span>
    </div>
    <div className="mt-1 text-[11.5px] text-black/40">{sub}</div>
    <div className="mt-4 h-[2px] w-full rounded-full" style={{ background: `linear-gradient(90deg, ${accent} 0%, ${accent}40 60%, transparent 100%)` }} />
  </div>
);

/* ═════════════════════════ Compteur circulaire (deprecated V3 — kept for rollback) ═ */
const CircleCounter = ({ value, label, sub, ratio, testId }) => {
  const size = 96; const stroke = 5; const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r; const clamp = Math.max(0, Math.min(1, ratio ?? 0.4)); const dash = c * clamp;
  return (
    <div className="flex flex-col items-center" data-testid={testId}>
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="rgba(0,0,0,0.08)" strokeWidth={stroke} />
          <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#C8FF2F" strokeWidth={stroke}
                  strokeLinecap="round" strokeDasharray={`${dash} ${c - dash}`} />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-[36px] font-medium tracking-[-0.02em] text-black">{value}</span>
        </div>
      </div>
      <div className="mt-4 text-[10.5px] uppercase tracking-[0.16em] text-black/55">{label}</div>
      <div className="mt-1 text-[11px] text-black/40">{sub}</div>
    </div>
  );
};

/* ═════════════════════════ Row ═════════════════════════════ */
const ARCH_ACCENT = { payment: "#F59E0B", response: "#8B5CF6", delivery: "#C8FF2F", other: "#94A3B8" };

const ActionRow = ({ item, isPriority, onOpen, totals }) => {
  const arch = archetypeOf(item, totals);
  const silhouette = guessSilhouette(item.vehicle_name);
  const accent = ARCH_ACCENT[arch.kind] || ARCH_ACCENT.other;
  const timeCtx = arch.kind === "payment" && item.delivery_date
    ? `Livraison dans ${daysUntil(item.delivery_date)} jour${daysUntil(item.delivery_date) > 1 ? "s" : ""}`
    : arch.kind === "response" ? (item.last_client_message_at ? humanAgo(item.last_client_message_at) : "")
    : arch.kind === "delivery" ? "À planifier" : "";
  // Sprint P0.1 — le CTA "Répondre" ouvre le fil messages via deep-link #messages
  const wantsMessages = arch.kind === "response";
  const openTarget = (id) => onOpen(id, wantsMessages ? "messages" : null);

  return (
    <article
      className="relative flex items-center gap-6 pl-4"
      data-testid={`dashboard-v3-row-${item.id}`}
    >
      {/* Ribbon latérale — accent famille */}
      <span
        className="absolute left-0 top-3 bottom-3 w-[3px] rounded-full"
        style={{ background: accent, opacity: isPriority ? 0.85 : 0.35 }}
      />
      <button
        onClick={() => openTarget(item.id)}
        className="relative h-[96px] w-[152px] flex-shrink-0 overflow-hidden rounded-2xl bg-[#0C0C0C]"
        data-testid={`row-photo-${item.id}`}
        aria-label={`Ouvrir dossier ${item.vehicle_name}`}
      >
        {item.vehicle_image ? (
          <img src={item.vehicle_image} alt={item.vehicle_name}
               className="h-full w-full object-cover" loading="lazy"
               onError={(e) => { e.currentTarget.style.display = "none"; }} />
        ) : (
          <div className="flex h-full w-full items-center justify-center"
               style={{ background: "radial-gradient(circle at 50% 60%, #1c1c1c 0%, #0a0a0a 75%)" }}>
            <SilhouetteVehicle type={silhouette} variant="dark" className="w-[78%]" />
          </div>
        )}
      </button>

      <button onClick={() => openTarget(item.id)} className="min-w-0 max-w-[440px] flex-1 text-left">
        <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-0.5">
          <h3 className="text-[18px] font-medium tracking-[-0.01em] text-black">{item.vehicle_name}</h3>
          <span className="truncate text-[13.5px] text-black/40">{item.client_name}</span>
        </div>
        <div className="mt-2 text-[13px] text-black/60">{arch.reason}</div>
      </button>

      {timeCtx && (
        <div className="hidden text-[12.5px] text-black/45 lg:block">{timeCtx}</div>
      )}

      <div className="flex-shrink-0">
        {isPriority ? (
          <button
            onClick={() => openTarget(item.id)}
            className="inline-flex items-center gap-2 rounded-full bg-[#C8FF2F] px-5 py-2.5 text-[13px] font-medium text-black transition-transform hover:scale-[1.01] active:scale-[0.98]"
            style={{ boxShadow: "0 4px 10px -6px rgba(200,255,47,0.4), 0 1px 2px rgba(0,0,0,0.04)" }}
            data-testid={`cta-priority-${item.id}`}
          >
            {arch.cta}
            <ArrowRight size={14} />
          </button>
        ) : (
          <button
            onClick={() => openTarget(item.id)}
            className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-[12.5px] font-medium text-black/75 transition-colors hover:text-black"
            style={{ boxShadow: "0 1px 2px rgba(0,0,0,0.03), inset 0 0 0 1px rgba(0,0,0,0.05)" }}
            data-testid={`cta-ghost-${item.id}`}
          >
            {arch.cta}
          </button>
        )}
      </div>
    </article>
  );
};

const Skel = () => (
  <div className="flex items-center gap-6 pl-4">
    <div className="h-[96px] w-[152px] animate-pulse rounded-2xl bg-black/[0.04]" />
    <div className="min-w-0 flex-1 max-w-[440px] space-y-2.5">
      <div className="h-4 w-2/5 animate-pulse rounded-full bg-black/[0.05]" />
      <div className="h-3 w-1/3 animate-pulse rounded-full bg-black/[0.04]" />
    </div>
    <div className="h-9 w-28 animate-pulse rounded-full bg-black/[0.04]" />
  </div>
);

/* ═════════════════════════ MAIN PAGE ═══════════════════════ */
export default function DashboardV3() {
  const navigate = useNavigate();
  const { user, agency } = useAuth();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    api.getDashboardSummary(null)
      .then((s) => { if (!cancel) setSummary(s); })
      .catch(() => { if (!cancel) setSummary(null); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, []);

  const actions = useMemo(() => (summary?.priority_actions || []).slice(0, 5), [summary]);
  const remaining = useMemo(() => Math.max(0, (summary?.priority_actions || []).length - 5), [summary]);
  const totals = summary?.totals || {};

  const counts = useMemo(() => {
    const p = actions.filter((a) => archetypeOf(a, totals).kind === "payment").length;
    const r = actions.filter((a) => archetypeOf(a, totals).kind === "response").length;
    const d = actions.filter((a) => archetypeOf(a, totals).kind === "delivery").length;
    const blocked = totals?.blocked || 0;
    return {
      payment:  { value: p, accent: "#F59E0B" },
      response: { value: r, accent: "#8B5CF6" },
      delivery: { value: d, accent: "#C8FF2F" },
      blocked:  { value: Math.min(99, blocked), accent: "#EF4444" },
    };
  }, [actions, totals]);

  const today = summary?.today;
  const dateLine = (() => {
    const d = today ? new Date(today) : new Date();
    const wk = d.toLocaleDateString("fr-FR", { weekday: "long" }).toUpperCase();
    const dm = d.toLocaleDateString("fr-FR", { day: "numeric", month: "long" }).toUpperCase();
    return `${wk} · ${dm}`;
  })();

  return (
    <div
      className="relative min-h-screen text-black"
      style={{
        background:
          "radial-gradient(1000px 600px at 100% 0%, rgba(200,255,47,0.04) 0%, rgba(200,255,47,0) 45%), linear-gradient(180deg, #FAFAF7 0%, #F5F4EF 100%)",
      }}
      data-testid="dashboard-v3-page"
    >
      <AppSidebar agency={agency} user={user} testId="dashboard-v3-sidebar" />

      <main className="lg:pl-[260px]">
        <div className="mx-auto max-w-[1120px] px-10 py-12">
          {/* Header : date · titre "Pilotage" · sous-titre agence · CTA */}
          <div className="flex items-start justify-between gap-6">
            <div className="min-w-0 flex-1">
              <div className="text-[11px] uppercase tracking-[0.16em] text-black/45">
                {dateLine}
              </div>
              <h1 className="mt-3 text-[38px] font-semibold leading-[1.02] tracking-[-0.02em] text-black">
                Pilotage
              </h1>
              <p className="mt-1.5 text-[14px] text-black/50">
                {summary?.agency?.name || agency?.name || "wyvio"} · Vue agence
              </p>
            </div>

            <button
              onClick={() => navigate("/app/dossiers")}
              className="inline-flex flex-shrink-0 items-center gap-2 rounded-full bg-white px-4 py-2 text-[13px] font-medium text-black transition-colors"
              style={{ boxShadow: "0 1px 2px rgba(0,0,0,0.04), inset 0 0 0 1px rgba(0,0,0,0.08)" }}
              data-testid="dashboard-v3-new-dossier"
            >
              <Plus size={14} /> Nouveau dossier
            </button>
          </div>

          {/* KPI géants — 4 tuiles typographiques */}
          <section
            className="mt-10 grid grid-cols-2 gap-8 rounded-3xl bg-white/60 px-8 py-8 sm:grid-cols-4 sm:gap-10"
            style={{ boxShadow: "0 1px 2px rgba(0,0,0,0.02), inset 0 0 0 1px rgba(0,0,0,0.04)" }}
            data-testid="dashboard-v3-kpi"
          >
            <KpiGiant value={counts.payment.value}  accent={counts.payment.accent}  label="Paiements" sub="à confirmer" testId="kpi-payment" />
            <KpiGiant value={counts.response.value} accent={counts.response.accent} label="Messages"  sub="à traiter"   testId="kpi-response" />
            <KpiGiant value={counts.delivery.value} accent={counts.delivery.accent} label="Livraison" sub="à planifier" testId="kpi-delivery" />
            <KpiGiant value={counts.blocked.value}  accent={counts.blocked.accent}  label="Bloqués"   sub="dossiers"    testId="kpi-blocked" />
          </section>

          {/* Tabs À traiter / Priorités */}
          <div className="mt-12 flex items-baseline gap-6 border-b border-black/[0.06] pb-3">
            <span className="text-[11px] font-medium uppercase tracking-[0.2em] text-black">À traiter</span>
            <button className="text-[11px] uppercase tracking-[0.18em] text-black/35 hover:text-black/60">
              Priorités du jour
            </button>
          </div>

          {/* Rows — dossiers flottants, sans card, séparés par le vide */}
          <section className="mt-6 space-y-8" data-testid="dashboard-v3-rows">
            {loading && [0,1,2,3,4].map((i) => <Skel key={i} />)}
            {!loading && actions.length === 0 && (
              <div className="py-16 text-center text-[14px] text-black/45">
                Aucune action prioritaire pour l&apos;instant.
              </div>
            )}
            {!loading && actions.map((item, i) => (
              <ActionRow
                key={item.id}
                item={item}
                totals={totals}
                isPriority={i === 0}
                onOpen={(id, hash) => navigate(`/app/dossier/${id}${hash ? `#${hash}` : ""}`)}
              />
            ))}
          </section>

          {/* Autres dossiers */}
          {remaining > 0 && (
            <div className="mt-8 flex justify-start pl-4">
              <button
                onClick={() => setShowAll((v) => !v)}
                className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-[12.5px] font-medium text-black/65 transition-colors hover:text-black"
                style={{ boxShadow: "0 1px 2px rgba(0,0,0,0.04), inset 0 0 0 1px rgba(0,0,0,0.06)" }}
                data-testid="dashboard-v3-more-btn"
              >
                {remaining} autres dossier{remaining > 1 ? "s" : ""} actif{remaining > 1 ? "s" : ""}
                <ChevronDown size={14} className={showAll ? "rotate-180 transition-transform" : "transition-transform"} />
              </button>
            </div>
          )}

          {showAll && (
            <div className="mt-4 space-y-1">
              {(summary?.priority_actions || []).slice(5, 20).map((item) => (
                <button
                  key={item.id}
                  onClick={() => navigate(`/app/dossier/${item.id}`)}
                  className="flex w-full items-center gap-4 rounded-lg px-3 py-2 text-left transition-colors hover:bg-black/[0.03]"
                  data-testid={`dashboard-v3-more-row-${item.id}`}
                >
                  <div className="h-9 w-14 flex-shrink-0 overflow-hidden rounded-lg bg-black/[0.06]" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[13px] text-black">
                      {item.vehicle_name}
                      <span className="ml-2 text-black/40">{item.client_name}</span>
                    </div>
                  </div>
                  <ArrowRight size={14} className="text-black/30" />
                </button>
              ))}
            </div>
          )}

          {/* M6 · Pilotage Mandats · Actions (scoped premium design) */}
          <PilotageMandatsPanel />

          <div className="h-24" />
        </div>
      </main>
    </div>
  );
}
